import autobind from 'class-autobind';
import STATUS_CODES from 'builtin-status-codes';

import moment from 'moment';

import has from 'lodash/has';
import isNil from 'lodash/isNil';
import extend from 'lodash/extend';
import isArray from 'lodash/isArray';
import isEmpty from 'lodash/isEmpty';

import {
  sendRequestWithPromise
} from 'zoomforth_zfutils/xhr_tools.js';


export const ANALYTICS_URL_ROOT = '/api/analytics';


class AnalyticsDataFetcher {

  constructor() {
    this._endpoint_url = '';
    this._resource = '';
    this._params = {};
    this._filters = [];
    this._group_by = [];
    this._for_object = null;
    this._event_collection = null;

    autobind(this);
  }

  request({ endpoint_url='' }={}) {
    /*
      Starting a request zeroes out all
      previous object data meaning that
      any requests that haven't executed
      a _fetch call will be thrown away
      after this method is called.

      Multiple fetches can be made with one
      call to request, but that might add
      confusion if you're not verifying the
      input params.

      Typically you would want to make one
      fetch for one request.

      Another thing to consider, most data
      will be fetched in a lifecycle method.
      This can lead to malformed requests
      because the data fetcher builds queries
      in a non-linear fashion.

      If you want to make sure that requests
      do not contaminate one another, do not
      spread one request out over several
      functions. A request must be constructed
      and fetched in the same method, especially
      if it is a lifecycle.
    */
    if (endpoint_url === '') {
      this._endpoint_url = ANALYTICS_URL_ROOT;
    } else {
      this._endpoint_url = endpoint_url;
    }

    this._resource = '';
    this._params = {};
    this._filters = [];
    this._group_by = [];
    this._for_object = null;
    this._event_collection = null;

    return this;
  }

  _validate() {
    // add more validation, call this
    if (isNil(this._params.operation)) {
      throw new Error('Operation parameter must not be nil');
    }

    if (this._endpoint_url === '' || isNil(this._resource)) {
      throw new Error('Please specify a resource to query.');
    }
  }

  useCurrentTimezone() {
    try {
      return Intl.DateTimeFormat().resolvedOptions().timeZone;
    } catch (e) {
      return -(new Date()).getTimezoneOffset() * 60;
    }
  }

  visits({ includeUnique=false }) {
    this._resource = 'visits';
    this._params.include_unique = includeUnique;

    return this;
  }

  subpageVisits(subpageId) {
    if (isNil(subpageId)) {
      throw new Error(`subpage-visits resource requires subpageId to not be nil; Found ${subpageId}`);
    }

    this._resource = 'subpage-visits';
    this._params.subpage_id = subpageId;

    return this;
  }

  visitEvents(pageSessionUuid) {
    if (isNil(pageSessionUuid)) {
      throw new Error(`visit-events resource requires pageSessionUuid to not be nil; Found ${pageSessionUuid}`);
    }

    this._resource = 'visit-session';
    this._params.page_session_uuid = pageSessionUuid;

    return this;
  }

  page_activities() {
    this._event_collection = 'page_activities';

    return this;
  }

  device_visits() {
    this._resource = 'device-visits';

    return this;
  }

  visitors() {
    this._resource = 'visitors';

    return this;
  }

  visitor_sessions() {
    this._resource = 'visitor-sessions';

    return this;
  }

  sites() {
    this._resource = 'sites';

    return this;
  }

  sites_visits() {
    this._resource = 'sites/visits';

    return this;
  }

  subpages() {
    this._resource = 'subpages';

    return this;
  }

  team_usage() {
    this._resource = 'team-usage';

    return this;
  }

  content() {
    this._resource = 'content';

    return this;
  }

  sections() {
    this._resource = 'subpages';

    return this;
  }

  referrals() {
    this._resource = 'referrals';

    return this;
  }

  uploads() {
    this._resource = 'uploads';

    return this;
  }

  params(params) {
    if (has(params, 'operation')) console.warn('"operation" will be overridden on query execution, refrain from setting it here');

    if (!has(this._params, 'startDate')) {
      if (!has(params, 'startDate') || !moment.isMoment(params.startDate)) {
        throw new Error(`"startDate" is a required param for analytics requests and must be a valid moment object; Found ${typeof params.startDate}`);
      }
    }
    
    if (!has(this._params, 'endDate')) {
      if (!has(params, 'endDate') || !moment.isMoment(params.endDate)) {
        throw new Error(`"endDate" is a required param for analytics requests and must be a valid moment object; Found ${typeof params.endDate}`);
      }
    }

    if (!isEmpty(this._params)) {
      extend(this._params, params);
    } else {
      this._params = {
        startDate: params.startDate.format(),
        endDate: params.endDate.format(),
        ...params,
        filter_by_resource: true
      };
    }

    return this;
  }

  property_names(propertyNames) {
    this._params.property_names = propertyNames;

    return this;
  }

  forObject({ object, objectId }={}) {
    this._for_object = { object, objectId };

    return this;
  }

  filter(filters=[]) {
    if (!isArray(filters)) throw new Error(`@Param "filters" must be an array; Found: ${typeof filters}`);

    this._filters = filters;

    return this;
  }

  group_by(groupBy=[]) {
    if (!isArray(groupBy)) throw new Error(`@Param "groupBy" must be an array; Found: ${typeof groupBy}`);

    this._group_by = groupBy;

    return this;
  }

  extract() {
    this._params.operation = 'extraction';

    return this._fetch();
  }

  count() {
    this._params.operation = 'count';

    return this._fetch();
  }

  count_unique() {
    this._params.operation = 'count_unique';

    return this._fetch();
  }

  sum() {
    this._params.operation = 'sum';

    return this._fetch();
  }

  maximum() {
    this._params.operation = 'maximum';

    return this._fetch();
  }

  _fetch() {
    this._validate();

    var target_endpoint = this._endpoint_url;

    if (this._endpoint_url !== '' && this._resource !== '') {
      target_endpoint = `${this._endpoint_url}/${this._resource}`;
    }

    if (this._for_object) {
      if (has(this._for_object, 'object') && has(this._for_object, 'objectId')) {
        const { object, objectId } = this._for_object;
        target_endpoint = `${target_endpoint}/${object}/${objectId}`;
      }
      // else error
    }

    const defer = $.Deferred();

    sendRequestWithPromise({
      url: target_endpoint,
      method: 'POST',
      params: {
        filters: this._filters,
        group_by: this._group_by,
        resource: this._resource,
        event_collection: this._event_collection,
        ...this._params
      }
    })
      .then((response)=> {
        if (response) {
          if (response.results) {
            defer.resolve(response.results);
          } else {
            /* we also get reports
              download responses
              which don't have
              results property */
            defer.resolve(response);
          }
        }
      })
      .catch(({ status, response })=> {
        const hasStatusCode = !(isNil(status) || status === 0);

        var errorInfo = 'No message provided.';

        if (status === 400) {
          try{
            const msg = JSON.parse(response).error.message;
            if (msg && msg !== '') {
              errorInfo = msg;
            }
          } catch (e){ /* noop */ }
        }

        const error = {
          statusCode: status,
          title: (
            hasStatusCode ?
              `${status} ${STATUS_CODES[status]}`
          :
            'Unknown Request Error'
          ),
          description: (
            !hasStatusCode ?
              'Unable to connect to Zoomforth servers. You might be disconnected from the Internet.'
            : status === 400 ?
              `Something about the most recent request was rejected by the server with the following message: "${errorInfo}"`
            : status === 401 ?
              'Something failed because of bad authorization. Please contact us if this problem persists.'
            : status === 403 ?
              'It looks like you have been logged out. This might occur because you logged out in another tab or your session expired.'
            : status === 500 ?
              'This is a problem on our end. Our engineering team automatically receives a notification when this error occurs. However, if you contact support and explain what you were doing when this error occurred, it will speed up finding a fix.'
            :
              'This was an unexpected error. Please report it to the Zoomforth Support Team.'
          )
        };

        defer.reject(error);
      });

    return defer.promise();
  }
}

export default AnalyticsDataFetcher;
